import { useEffect, useState } from "react";
import { DateTime } from "luxon";
import { BannerService } from "@services/banners/bannerService";
import type { CurrentBanner } from "@services/banners/commands/cmdGetCurrentBanner";

/** setTimeout fires at once for a longer delay (2^31-1 ms, about 24.8 days). */
export const MAX_TIMEOUT_MS = 2_147_483_647;

const msUntilEnd = (b: CurrentBanner) =>
    b.ends_at ? DateTime.fromISO(b.ends_at).toMillis() - Date.now() : Infinity;

/**
 * The Live banner for the Partner Dashboard (MPN-2266). Read once when enabled; off (flag
 * off, or a preview) it makes no request. Fails silently: clients never see a banner error.
 * Hides itself when ends_at passes while the page is open. No polling: a newly live banner
 * shows on the next load.
 */
export function useCurrentBanner(enabled: boolean) {
    const [banner, setBanner] = useState<CurrentBanner | null>(null);

    useEffect(() => {
        if (!enabled) return;
        let live = true;
        new BannerService().getCurrentBanner().then(
            (b) => {
                if (live) setBanner(b && msUntilEnd(b) > 0 ? b : null);
            },
            (err: unknown) => {
                console.error("Error loading banner:", err);
            },
        );
        return () => {
            live = false;
        };
    }, [enabled]);

    useEffect(() => {
        if (!banner) return;
        const ms = msUntilEnd(banner);
        if (ms <= 0) {
            setBanner(null);
            return;
        }
        if (ms > MAX_TIMEOUT_MS) return;
        const t = setTimeout(() => setBanner(null), ms);
        return () => clearTimeout(t);
    }, [banner]);

    return { banner, hide: () => setBanner(null) };
}
